import { useState } from "react";
import { ClipboardList, MoreHorizontal, NotebookPen, Send, UserRoundCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FEEDBACK_MODES, fmtDate, fmtDateTime } from "@/lib/checkins";
import { errMsg } from "../staff/ResolveDialog";
import { DiaryProgressDialog } from "./DiaryProgressDialog";

const STATUS_CLS = { active: "bg-emerald-100 text-emerald-900", paused: "bg-amber-100 text-amber-900",
  ended: "bg-zinc-200 text-zinc-700", opted_out: "bg-red-100 text-red-900" };
const STATUS_TXT = { active: "Active", paused: "Paused", ended: "Ended", opted_out: "Opted out" };

const Fact = ({ label, children, testId }) => (
  <div><div className="text-2xs font-semibold uppercase tracking-[0.15em] text-zinc-500">{label}</div><div className="text-sm text-zinc-900" data-testid={testId}>{children}</div></div>
);

function inviteText(inv) {
  if (!inv) return "None open";
  const d = { sent: "emailed", failed: "email failed", pending: "sending", not_emailed: "in-clinic link" }[inv.delivery_status] || inv.delivery_status;
  const n = inv.kind === "diary" ? ` · ${inv.diary_entry_count} ${inv.diary_entry_count === 1 ? "entry" : "entries"} so far` : "";
  return `${inv.status === "started" ? "Started" : "Open"} · ${d}${inv.reminder_count ? " · reminded" : ""}${n}`;
}

export function EnrolmentCard({ enrolment: e, readOnly, onAction, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [viewDiary, setViewDiary] = useState(false);
  const isDiary = e.kind === "diary";
  async function sendNow() {
    setBusy(true);
    try {
      const r = await api.post(`/checkin-enrolments/${e.id}/send-now`);
      if (r.data.ok) toast.success(r.data.resent ? "Fresh link emailed for the open progress review" : "Progress review emailed");
      else toast.error("Email could not be delivered", { description: r.data.error || "Check the email settings or complete it with the client in clinic." });
      onChanged();
    } catch (err) {
      toast.error("Couldn't send", { description: errMsg(err) });
    } finally {
      setBusy(false);
    }
  }
  async function assisted() {
    try {
      const r = await api.post(`/checkin-enrolments/${e.id}/assisted`);
      window.open(`/exercise-progress-review/${r.data.token}`, "_blank", "noopener");
      onChanged();
    } catch (err) {
      toast.error("Couldn't start", { description: errMsg(err) });
    }
  }
  const live = ["active", "paused"].includes(e.status);
  const items = [
    e.status === "active" && ["pause", "Pause"], e.status === "paused" && ["resume", "Resume"],
    live && !isDiary && ["cadence", "Change cadence"], live && !isDiary && ["feedback_mode", "Change feedback release"],
    live && ["practitioner", "Change practitioner"], live && ["end", "End enrolment"],
    !live && ["reenrol", "Re-enrol (fresh consent)"],
  ].filter(Boolean);
  return (
    <Card className="p-4" data-testid={`checkin-enrolment-${e.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <ClipboardList className="h-4 w-4 text-zinc-500" />
        <span className="font-display font-semibold text-zinc-950">{e.template_name}</span>
        <span className="font-mono-num text-xs text-zinc-500">v{e.version}</span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_CLS[e.status]}`} data-testid="checkin-enrolment-status">{STATUS_TXT[e.status]}</span>
        {!readOnly && (
          <div className="ml-auto flex gap-2">
            {isDiary && e.open_invitation && <Button size="sm" variant="outline" onClick={() => setViewDiary(true)} data-testid="checkin-diary-progress-btn"><NotebookPen className="mr-1.5 h-3.5 w-3.5" />Diary so far</Button>}
            {e.status === "active" && <Button size="sm" variant="outline" disabled={busy} onClick={sendNow} data-testid="checkin-send-now-btn"><Send className="mr-1.5 h-3.5 w-3.5" />{isDiary ? "Email link" : "Send now"}</Button>}
            {live && <Button size="sm" variant="outline" onClick={assisted} data-testid="checkin-assisted-btn"><UserRoundCheck className="mr-1.5 h-3.5 w-3.5" />Complete with client</Button>}
            <DropdownMenu>
              <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" data-testid="checkin-enrolment-menu"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {items.map(([k, label]) => <DropdownMenuItem key={k} onClick={() => onAction(e, k)} data-testid={`checkin-action-${k}`}>{label}</DropdownMenuItem>)}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {isDiary ? <Fact label="Type" testId="checkin-diary-type">{e.diary_days}-day diary</Fact> : <Fact label="Cadence">Every {e.cadence_days / 7} weeks</Fact>}
        {isDiary
          ? <Fact label="Diary starts" testId="checkin-next-due">{fmtDate(e.open_invitation?.diary_start || e.start_at)}</Fact>
          : <Fact label="Next due" testId="checkin-next-due">{live ? fmtDate(e.next_due_at) : "—"}</Fact>}
        <Fact label="Last completed">{fmtDateTime(e.last_completed_at)}</Fact>
        <Fact label="Open progress review" testId="checkin-open-invitation">{inviteText(e.open_invitation)}</Fact>
        <Fact label="Practitioner">{e.practitioner_name || "—"}</Fact>
        <Fact label="Feedback">{isDiary ? "Reviewed at appointments" : FEEDBACK_MODES[e.feedback_mode]?.split(" — ")[0]}</Fact>
      </div>
      {viewDiary && <DiaryProgressDialog iid={e.open_invitation.id} onClose={() => setViewDiary(false)} />}
    </Card>
  );
}
